import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { AgentClient } from "./AgentClient";
import { HttpAgentClient } from "./HttpAgentClient";
import { MockAgentClient } from "./MockAgentClient";
import { withAgentTracking } from "./trackedClient";
import { API_BASE, API_KEY } from "../analytics/config";

/** Gateway agents settings base. `API_BASE` is the gateway mount root (e.g.
 *  "/coding-gateway/internal" or "https://api.ai21.com/tokenwise"); the settings
 *  CRUD and the analytics API are sibling sub-paths under it, so we append
 *  `/settings/agents` the same way the analytics client appends `/analytics/v1`. */
function settingsBase(): string | undefined {
  if (!API_BASE) return undefined;

  return `${API_BASE.replace(/\/+$/, "")}/settings/agents`;
}

const AgentClientContext = createContext<AgentClient | null>(null);

/** Real settings client when the gateway is configured; otherwise the mock. */
export function createDefaultAgentClient(): AgentClient {
  const base = settingsBase();
  const client = base
    ? new HttpAgentClient(base, API_KEY)
    : new MockAgentClient();
  return withAgentTracking(client);
}

export function AgentProvider({
  children,
  client,
}: {
  children: ReactNode;
  client?: AgentClient;
}) {
  const value = useMemo(() => client ?? createDefaultAgentClient(), [client]);
  return (
    <AgentClientContext.Provider value={value}>
      {children}
    </AgentClientContext.Provider>
  );
}

export function useAgentClient(): AgentClient {
  const client = useContext(AgentClientContext);
  if (!client) {
    throw new Error("useAgentClient must be used within an AgentProvider");
  }

  return client;
}
